import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useAuth from './useAuth';
import { lessonsProgressesQueryKeys } from '../services/querykeys';
import { useEffect } from 'react';
import useInfiniteService from './useInfiniteService';
import { isStudent } from '@/functions/auth';
import { LessonProgressOrderBy } from '../services/lessonProgressServices';

type UseLessonProgressProps = {
  lessonIds?: number[];
  orderBy?: LessonProgressOrderBy;
};

export default function useLessonProgresses({
  lessonIds,
  orderBy = 'scheduled_lesson_order',
}: UseLessonProgressProps) {
  const { user } = useAuth();
  const pageSizeLimit = 15;
  const pageSize = lessonIds?.length || 25;
  const studentView = !!user && isStudent(user?.userType);
  const { queryFn, queryKey } = lessonsProgressesQueryKeys.list({
    lesson: lessonIds,
    studentId: user ? [user?.id] : [],
    pageSize: Math.min(pageSize, pageSizeLimit),
    orderBy,
  })._ctx.infinity;

  const {
    results: lessonProgresses,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteService({
    queryFn,
    queryKey,
    enabled: !!lessonIds?.length && studentView,
    keepPreviousData: true,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isLoading) return;
    fetchNextPage();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, isLoading]);

  return {
    lessonProgresses: lessonProgresses,
    queryKey,
    studentView,
    isLoading,
  };
}
